import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { payrollService } from '../../services/payrollService';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import EmployeeDashboard from './components/EmployeeDashboard';
import PayrollProcessing from './components/PayrollProcessing';
import TaxManagement from './components/TaxManagement';
import PayrollAlerts from './components/PayrollAlerts';
import PayrollReports from './components/PayrollReports';

const PayrollManagementSystem = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, loading: authLoading, profileLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [staffMembers, setStaffMembers] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [taxConfig, setTaxConfig] = useState([]);
  const [stats, setStats] = useState({
    totalPayroll: 0,
    totalDeductions: 0,
    totalNetPay: 0,
    pendingApprovals: 0
  });
  const [selectedPeriod, setSelectedPeriod] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1)?.toISOString()?.split('T')?.[0],
    end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)?.toISOString()?.split('T')?.[0]
  });

  const hasAccess = isGymOwner || isStaff;

  useEffect(() => {
    if (!authLoading && !profileLoading && (!user || !hasAccess)) {
      navigate('/member-dashboard');
    }
  }, [user, hasAccess, authLoading, profileLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && hasAccess && !profileLoading) {
      loadPayrollData();
    }
  }, [currentTenant, hasAccess, profileLoading, selectedPeriod]);

  const loadPayrollData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStaffMembers(),
        loadPayrollRecords(),
        loadAlerts(),
        loadTaxConfig(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading payroll data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStaffMembers = async () => {
    try {
      const { data, error } = await supabase?.from('tenant_members')?.select(`
          *,
          user_profiles!tenant_members_user_id_fkey(id, full_name, email, avatar_url, phone),
          staff_wage_rates!staff_wage_rates_staff_id_fkey(
            id, wage_type, hourly_rate, annual_salary, overtime_multiplier, effective_date
          )
        `)?.eq('tenant_id', currentTenant?.id)?.in('role', ['gym_owner', 'staff', 'trainer'])?.eq('is_active', true)?.order('user_profiles(full_name)');

      if (error) throw error;
      setStaffMembers(data || []);
    } catch (error) {
      console.error('Error loading staff members:', error);
    }
  };

  const loadPayrollRecords = async () => {
    try {
      const records = await payrollService?.getPayrollRecords(currentTenant?.id, {
        periodStart: selectedPeriod?.start,
        periodEnd: selectedPeriod?.end
      });
      setPayrollRecords(records || []);
    } catch (error) {
      console.error('Error loading payroll records:', error);
    }
  };

  const loadAlerts = async () => {
    try {
      const alertsData = await payrollService?.getPayrollAlerts(currentTenant?.id, {
        isResolved: false
      });
      setAlerts(alertsData || []);
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const loadTaxConfig = async () => {
    try {
      const config = await payrollService?.getTaxConfiguration(currentTenant?.id);
      setTaxConfig(config || []);
    } catch (error) {
      console.error('Error loading tax config:', error);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await payrollService?.getPayrollStats(
        currentTenant?.id,
        selectedPeriod?.start,
        selectedPeriod?.end
      );
      setStats(statsData || {
        totalPayroll: 0,
        totalDeductions: 0,
        totalNetPay: 0,
        pendingApprovals: 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handlePeriodChange = (start, end) => {
    setSelectedPeriod({ start, end });
  };

  const handleRefresh = () => {
    loadPayrollData();
  };

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading payroll system...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  const tabs = [
    { id: 'dashboard', label: 'Employee Dashboard', icon: 'Users' },
    { id: 'processing', label: 'Payroll Processing', icon: 'DollarSign' },
    { id: 'tax', label: 'Tax Management', icon: 'Calculator' },
    { id: 'alerts', label: 'Alerts', icon: 'AlertTriangle', badge: alerts?.length },
    { id: 'reports', label: 'Reports', icon: 'FileText' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-6 pb-24 lg:pb-6">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Payroll Management System</h1>
              <p className="text-muted-foreground">Comprehensive employee wage processing and payroll administration</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleRefresh}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Icon name="RefreshCw" size={18} />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Payroll</span>
              <Icon name="DollarSign" size={18} className="text-green-500" />
            </div>
            <p className="text-2xl font-bold text-foreground">${stats?.totalPayroll?.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Gross pay for period</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Deductions</span>
              <Icon name="Minus" size={18} className="text-red-500" />
            </div>
            <p className="text-2xl font-bold text-foreground">${stats?.totalDeductions?.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Taxes & deductions</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Net Pay</span>
              <Icon name="TrendingUp" size={18} className="text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-foreground">${stats?.totalNetPay?.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Take-home pay</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Pending Approvals</span>
              <Icon name="Clock" size={18} className="text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-foreground">{stats?.pendingApprovals}</p>
            <p className="text-xs text-muted-foreground mt-1">Awaiting approval</p>
          </div>
        </div>

        {/* Period Selector */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-2">
              <Icon name="Calendar" size={18} className="text-primary" />
              <span className="font-medium text-foreground">Pay Period:</span>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="date"
                value={selectedPeriod?.start}
                onChange={(e) => handlePeriodChange(e?.target?.value, selectedPeriod?.end)}
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              />
              <span className="text-muted-foreground">to</span>
              <input
                type="date"
                value={selectedPeriod?.end}
                onChange={(e) => handlePeriodChange(selectedPeriod?.start, e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              />
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-card border border-border rounded-lg mb-6 overflow-x-auto">
          <div className="flex">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap relative ${
                  activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
                {tab?.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {tab?.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-card border border-border rounded-lg p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          ) : (
            <>
              {activeTab === 'dashboard' && (
                <EmployeeDashboard
                  staffMembers={staffMembers}
                  payrollRecords={payrollRecords}
                  selectedPeriod={selectedPeriod}
                  onRefresh={handleRefresh}
                />
              )}
              {activeTab === 'processing' && (
                <PayrollProcessing
                  staffMembers={staffMembers}
                  selectedPeriod={selectedPeriod}
                  onRefresh={handleRefresh}
                />
              )}
              {activeTab === 'tax' && (
                <TaxManagement
                  taxConfig={taxConfig}
                  onRefresh={handleRefresh}
                />
              )}
              {activeTab === 'alerts' && (
                <PayrollAlerts
                  alerts={alerts}
                  onRefresh={handleRefresh}
                />
              )}
              {activeTab === 'reports' && (
                <PayrollReports
                  payrollRecords={payrollRecords}
                  selectedPeriod={selectedPeriod}
                  staffMembers={staffMembers}
                />
              )}
            </>
          )}
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default PayrollManagementSystem;